// src/slices/systemConfiguration/reducer.ts
// Redux slice for System Configuration state management

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SystemConfiguration, UrlValidationResult, SystemConfigState } from '../../pages/SystemConfiguration/types';
import { fetchSystemConfig, updateRegionConfig, updateLookupConfig, updateCommissionConfig, updateTaxConfig, updateCrossPrizePayoutConfig, updatePrizeClaimValidityConfig } from './thunk';

const initialState: SystemConfigState = {
  config: null,
  validationResult: null,
  loading: false,
  error: null,
  activeTab: 'region',
};

const systemConfigSlice = createSlice({
  name: 'SystemConfiguration',
  initialState,
  reducers: {
    setConfig: (state, action: PayloadAction<SystemConfiguration>) => {
      state.config = action.payload;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setValidationResult: (state, action: PayloadAction<UrlValidationResult>) => {
      state.validationResult = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<'region' | 'lookup'>) => {
      state.activeTab = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearValidationResult: (state) => {
      state.validationResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchSystemConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateRegionConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRegionConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateRegionConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateLookupConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLookupConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateLookupConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateCommissionConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCommissionConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateCommissionConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateTaxConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTaxConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateTaxConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateCrossPrizePayoutConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCrossPrizePayoutConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateCrossPrizePayoutConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updatePrizeClaimValidityConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePrizeClaimValidityConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(updatePrizeClaimValidityConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setConfig,
  setLoading,
  setError,
  setValidationResult,
  setActiveTab,
  clearError,
  clearValidationResult,
} = systemConfigSlice.actions;

export default systemConfigSlice.reducer;